import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, RefreshCcw, ShieldCheck, User as UserIcon, Star } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function JudgingLinkDialog({ open, onOpenChange, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pid = localStorage.getItem("sc_pid");
      const { data } = await api.get(`/productions/${pid}/judging-token`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load link");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open && isAdmin) load(); }, [open, isAdmin, load]);

  const rotate = async () => {
    if (!confirm("Rotate the judging link? Anyone using the current link will be locked out and must be given the new one.")) return;
    setRotating(true);
    try {
      const pid = localStorage.getItem("sc_pid");
      await api.post(`/productions/${pid}/judging-token/rotate`);
      toast.success("New link generated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not rotate"); }
    finally { setRotating(false); }
  };

  const fullUrl = data ? `${window.location.origin}${data.url_path}` : "";
  const copy = () => {
    try { navigator.clipboard?.writeText(fullUrl); toast.success("Link copied"); }
    catch { toast.info(fullUrl); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="judging-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /> Adjudicator link</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Share this link with external judges. They enter their name once and can score auditions without an account. They&apos;ll see profiles but never contact details or other judges&apos; scores.
          </DialogDescription>
        </DialogHeader>

        {!isAdmin && <div className="text-sm text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded p-3">Only admins can view or rotate the adjudicator link.</div>}

        {isAdmin && (
          loading || !data ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-gold/30 bg-gold/5 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-gold">Current link</div>
                <div className="font-mono text-xs bg-[#0A0A0A] rounded border border-white/10 px-3 py-2 break-all" data-testid="judging-url">{fullUrl}</div>
                <div className="flex items-center gap-2">
                  <button onClick={copy} data-testid="copy-judging-link" style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110">
                    <Copy className="w-3.5 h-3.5" /> Copy link
                  </button>
                  <button onClick={rotate} disabled={rotating} data-testid="rotate-judging-link"
                    className="h-9 px-3 rounded border border-red-500/40 text-red-300 text-sm inline-flex items-center gap-1.5 hover:bg-red-500/10 ml-auto">
                    <RefreshCcw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> {rotating ? "Rotating…" : "Rotate & revoke"}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Judges who have signed in ({data.judges.length})</div>
                {data.judges.length === 0 ? (
                  <div className="rounded border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500 bg-[#0F0F0F]">
                    No one has used the link yet.
                  </div>
                ) : (
                  <ul className="rounded border border-white/10 divide-y divide-white/5 max-h-64 overflow-y-auto">
                    {data.judges.map((j) => (
                      <li key={j.id} data-testid={`judge-row-${j.id}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <UserIcon className="w-4 h-4 text-zinc-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{j.name}</div>
                          {j.email && <div className="text-[11px] text-zinc-500 truncate">{j.email}</div>}
                        </div>
                        <div className="text-[11px] text-gold font-mono inline-flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> {j.score_count || 0}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
